// composables
import { ref, toRaw, type Ref } from 'vue'
// utilities
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
import {
  communicationTemplatePlaceholderTokens
} from '@/utilities/communications/templatePlaceholders'
import { formatNumber } from '@/utilities/number'
// stores
import { useAuthStore } from '@/stores/AuthStore'
// composables
import usePhoneValidator from '@/composables/usePhoneValidator'
import useCurrency from '@/composables/useCurrency'
// types
import type { Agreement, DealershipCommsConfigItem } from '@/types/domain/agreement'

/**
 * Composable for managing template placeholders
 * Handles placeholder initialization and replacement for email/SMS templates
 */
export default function useAgreementPlaceholders(
  agreementData: Ref<Agreement | null>,
  selectedCallCenterCommsConfig: Ref<DealershipCommsConfigItem | null>
) {
  const { n } = useI18n()
  const authStore = useAuthStore()
  const { formatCurrencyDisplay } = useCurrency()

  // state
  const placeholders = ref<Array<{ placeholder: string; value: string | number | null }>>([])

  // phone validator for dealership phone number
  const fromNumberValidator = usePhoneValidator()

  /**
   * Formats final repayment with symbol prefix and code suffix.
   * @param amount - Numeric repayment amount.
   * @param currencyCode - Optional ISO currency code.
   * @returns Formatted display string, for example "£10,000 GBP".
   */
  function formatAgreementFinalRepayment(
    amount: number | null | undefined,
    currencyCode: string | null | undefined
  ): string {
    try {
      if (amount == null) {
        return ''
      }

      const formattedAmount = formatNumber(amount, 'normal', 0) ?? String(amount)
      return formatCurrencyDisplay(formattedAmount, currencyCode)
    } catch (error) {
      console.error('Failed to format agreement final repayment', error)
      return ''
    }
  }

  /**
   * Initializes placeholders array with all available placeholders
   */
  function initPlaceholders(): void {
    if (!agreementData.value) {
      return
    }

    // Validate phone number if available
    if (selectedCallCenterCommsConfig.value?.phoneNumber) {
      fromNumberValidator.validatePhoneNumber('+' + selectedCallCenterCommsConfig.value.phoneNumber)
    }

    placeholders.value = [
      {
        placeholder: communicationTemplatePlaceholderTokens.emailGreeting,
        value: selectedCallCenterCommsConfig.value?.emailConfig?.greeting || ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.emailSignOff,
        value: selectedCallCenterCommsConfig.value?.emailConfig?.signOff || ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.onBehalfOf,
        value: selectedCallCenterCommsConfig.value?.emailConfig?.onBehalfOf || ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.smsGreeting,
        value: 'Hi {{customer_name}},'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.smsSignOff,
        value: '{{user_forename}} - {{dealership_name}}'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.customerName,
        value: agreementData.value.customer.name ?? 'customer'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.customerForename,
        value: agreementData.value.customer.isCompany
          ? agreementData.value.customer.companyName
          : agreementData.value.customer.forename
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.customerSurname,
        value: agreementData.value.customer.isCompany
          ? agreementData.value.customer.companyName
          : agreementData.value.customer.surname
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.agreementId,
        value: agreementData.value.id
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.agreementEndDate,
        value: dayjs(agreementData.value.endDate).format('DD/MM/YYYY')
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.agreementFinalRepayment,
        value: formatAgreementFinalRepayment(agreementData.value.finalRepayment, agreementData.value.currency)
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.agreementProductType,
        value: agreementData.value.productType
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.brandColor,
        value: agreementData.value.dealership.branding?.accentColor ?? null
      },
      {
        // Legacy alias: older saved templates still contain {{manufacturer_brand_color}}
        placeholder: communicationTemplatePlaceholderTokens.manufacturerBrandColor,
        value: agreementData.value.dealership.branding?.accentColor ?? null
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.agreementMonthsRemaining,
        value: agreementData.value.periodRemaining ?? ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.agreementProduct,
        value: agreementData.value.productType
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.vehicle,
        value: agreementData.value.vehicle
          ? `${agreementData.value.vehicle.make} ${agreementData.value.vehicle.model}`
          : 'vehicle'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.vehicleImageUrl,
        value: agreementData.value.vehicle.imageUrl
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.vehicleCategory,
        value: agreementData.value.vehicle.category
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.vehicleVrm,
        value: agreementData.value.vehicle.vrm
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.vehicleMake,
        value: agreementData.value.vehicle.make ?? ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.vehicleModel,
        value: agreementData.value.vehicle.model ?? ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.odometerAllowanceYearly,
        value: agreementData.value.odometer.allowanceYearly
          ? n(agreementData.value.odometer.allowanceYearly, 'default')
          : '-'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.odometerUnit,
        value: agreementData.value.odometer.unit ?? ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.odometerExpected,
        value: agreementData.value.odometer.expected ? n(agreementData.value.odometer.expected, 'default') : '-'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.dealershipName,
        value: agreementData.value.dealership.displayName ?? 'the dealership'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.dealershipAssignedPhoneNumber,
        value: fromNumberValidator.validatedData.value
          ? toRaw(fromNumberValidator.validatedData.value)?.number?.national || ''
          : ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.dealershipAssignedEmail,
        value: selectedCallCenterCommsConfig.value?.emailAddress || ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.dealershipAssignedSmsNumber,
        value: selectedCallCenterCommsConfig.value?.smsNumber || ''
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.userName,
        value: authStore.details.name ?? 'Engagement Team'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.userForename,
        value: authStore.details.forename ?? 'Engagement Team'
      },
      {
        placeholder: communicationTemplatePlaceholderTokens.userSurname,
        value: authStore.details.surname ?? 'Engagement Team'
      }
    ]
  }

  /**
   * Replaces placeholders in a string with their values
   * @param value - The string containing placeholders
   * @returns The string with placeholders replaced
   */
  function replacePlaceholders(value: string): string {
    let newValue = value
    let placeholdersFound = false

    placeholders.value.forEach(placeholder => {
      if (newValue?.includes(placeholder.placeholder)) {
        newValue = newValue.replace(placeholder.placeholder, String(placeholder.value))
        placeholdersFound = true
      }
    })

    // If any placeholders were found and replaced, check for nested placeholders
    if (placeholdersFound) {
      newValue = replacePlaceholders(newValue)
    }

    return newValue
  }

  /**
   * Converts HTML br tags to newlines
   * @param str - The string containing br tags
   * @returns The string with br tags converted to newlines
   */
  function brToNewLine(str: string): string {
    return str?.replace(/<br\s*\/?>/gm, '\n')
  }

  return {
    // state
    placeholders,
    // methods
    initPlaceholders,
    replacePlaceholders,
    brToNewLine
  }
}
